import classNames from 'classnames';
import Checkbox, {
  CheckboxProps,
} from '@/components/molecules/checkbox/checkbox';
import styles from './checkbox-group.module.scss';

export interface CheckboxOption extends CheckboxProps {
  departureCodes?: string[];
  filterIds?: number[];
  id: string;
  nodeType?: string;
}

export interface CheckboxGroupProps {
  /** The class name to apply */
  className?: string;
  /** The checkbox group name */
  groupName: string;
  /** An event that is triggered on change */
  onChange?: (updatedOptions: CheckboxOption[]) => void;
  /** An array of the filter cards to show */
  options: CheckboxOption[];
}

const CheckboxGroup = ({
  className,
  groupName,
  onChange,
  options,
}: CheckboxGroupProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    if (value === 'selectAll') {
      const updatedOptions = options.map(option => ({
        ...option,
        checked: checked && !option.disabled,
      }));

      onChange?.(updatedOptions);

      return;
    }

    const updatedOptions = options.map(option =>
      option.id === value ? { ...option, checked } : option,
    );

    onChange?.(updatedOptions);
  };

  const selectedCount = options.filter(
    option => option.checked && !option.disabled,
  ).length;

  const enabledOptionsCount = options.filter(option => !option.disabled).length;

  const isSelectAllChecked =
    selectedCount === options.filter(option => !option.disabled).length;

  const isSelectAllIndeterminate = selectedCount > 0 && !isSelectAllChecked;

  const isSelectAllDisabled = enabledOptionsCount === 0;

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.heading}>
        <Checkbox
          checked={isSelectAllChecked && !isSelectAllDisabled}
          disabled={isSelectAllDisabled}
          groupHeader={true}
          label={groupName}
          onChange={handleChange}
          partial={isSelectAllIndeterminate}
          tagStyle={'bodyLargeBold'}
          value="selectAll"
        />
      </div>

      <div className={styles.optionsContainer}>
        {options.map(option => (
          <Checkbox
            key={option.id}
            checked={!!option.checked && !option.disabled}
            disabled={option.disabled}
            label={option.label}
            onChange={handleChange}
            tagStyle={'bodyMedium'}
            value={option.id}
          />
        ))}
      </div>
    </div>
  );
};

CheckboxGroup.displayName = 'CheckboxGroup';

export default CheckboxGroup;
